import React, { useContext, type FC, type PropsWithChildren } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import { createRouterSelector } from '@atlassian/react-resource-router/src/controllers/router-store/index.tsx';
import { isCircuitOpen } from '@jira/platform__cookie-circuit-breaker/src/is-circuit-open.tsx';
import type { CookieCircuitBreakerConfig } from '@jira/platform__routing-route-types/src/JiraSpaRoute.tsx';
import { SSR_FALLBACK_MARKER_ATTRIBUTE } from '@jira/ssr__ssr-failure-tracker/src/tracker.tsx';
import { detectionBoundary } from './detectionBoundary.tsx';
import { FallbackIndicator } from './FallbackIndicator.tsx';
import type { SSRFailureProps } from './SSRFailureProps.types.tsx';

const useCircuitBreakerConfigRouteSelector: () => CookieCircuitBreakerConfig | undefined =
	createRouterSelector((state) => state.route?.bifrost?.circuitBreakerConfig);
// eslint-disable-next-line camelcase
export { useCircuitBreakerConfigRouteSelector as useCircuitBreakerConfigRouteSelector__EXPORTED_FOR_TESTING };

/**
 * Reports a SSR failure on render. To be placed in (top level) Suspense fallback
 * 🚨Single use per route 🚨
 */
export const ReportSSRFailOnUse: FC<
	{
		ignoreWhenCircuitOpen?: boolean;
		/**
		 * @deprecated DO NOT USE unless you know what you are doing; reports failure even if DisableSSRFailDetection is in the tree above this.
		 * You might use this to indicate that a certain nested suspense boundary is critical to your page load, even if it is not the top-level one
		 */
		UNSAFE__forceReporting?: boolean;
	} & SSRFailureProps
> = ({
	ignoreWhenCircuitOpen,
	UNSAFE__forceReporting = false,
	reportToSlackChannel,
	fallbackId,
	displayErrorInStaging,
}) => {
	const circuitBreakerConfig = useCircuitBreakerConfigRouteSelector();
	const isCircuitOpenValue = isCircuitOpen(circuitBreakerConfig);
	const isCookieCircuitBreakerOpen =
		ignoreWhenCircuitOpen && isCircuitOpenValue && circuitBreakerConfig?.isCircuitBreakerEnabled;

	const shouldReport = useContext(detectionBoundary) || UNSAFE__forceReporting;

	if (!isCookieCircuitBreakerOpen) {
		// Under streaming a fallback render is not terminal, so the legacy `useMemo` side-effect above is disabled;
		// instead emit an inert marker that the streaming renderer (`markSsrFailuresFromHtml`) correlates with React's
		// boundary-completion (`$RC`) scripts to decide whether this fallback was the terminal SSR output.
		// Same skip semantics as the legacy path - only when detection is active for this boundary.
		if (shouldReport && fg('jfp-magma-ssr-streaming-monitoring-fixes')) {
			return <template {...{ [SSR_FALLBACK_MARKER_ATTRIBUTE]: fallbackId ?? '' }} />;
		}
		return null;
	}

	return (
		<FallbackIndicator
			reportToSlackChannel={reportToSlackChannel}
			fallbackId={fallbackId}
			displayErrorInStaging={displayErrorInStaging}
			isCookieCircuitBreakerFailure={isCookieCircuitBreakerOpen}
			cookieKey={circuitBreakerConfig?.cookieKey}
		/>
	);
};

/**
 * Disables SSR render failure detection.
 * To be placed inside Suspense to aid `RLL ssr:false` components
 */
export const DisableSSRFailDetection: FC<PropsWithChildren> = ({ children }) =>
	__SERVER__ ? (
		<detectionBoundary.Provider value={false}>{children}</detectionBoundary.Provider>
	) : (
		<>{children}</>
	);
